<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <p class="mb-0 main-identifier fw-semibold">PV Memo</p>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View PV Memos</p>
    </div>

    <div class="d-flex justify-content-center">
      <div class="form-outer" style="max-width: 1400px;">
        <div class="view-item position-relative">

          <div class="form-inner">
            <div class="form-header d-flex justify-content-between align-items-center report">
              <div>
                <h1 class="mb-0">PV Memo Directory</h1>
                <p class="mb-0 small">Manage and track PV Memos.</p>
              </div>
            </div>

            <div class="form-body">
              <!-- Filters -->
              <div class="filters-section mb-4">
                <div class="row g-3">
                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">MDA:</label>
                    <SearchableSelect v-model="filters.mda" :options="mdaFilterOptions" placeholder="All" small
                      clearable />
                  </div>

                  <div class="col-md-3">
                    <label class="form-label small fw-semibold">Date Created:</label>
                    <select class="form-select form-select-sm" v-model="filters.date">
                      <option value="">All Time</option>
                      <option value="this_month">This Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="this_year">This Year</option>
                    </select>
                  </div>

                  <div class="col-md-3 d-flex align-items-end">
                    <div class="w-100">
                      <label class="form-label small fw-semibold invisible">Action</label>
                      <button class="btn btn-sm btn-outline-secondary w-100" @click="clearFilters">
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table Controls -->
              <div v-if="!loading" class="table-controls">
                <div class="table-controls-top">
                  <div class="table-controls-left">
                    <div class="entries-control">
                      <span>Show</span>
                      <select class="form-select form-select-sm" v-model.number="pageLength">
                        <option :value="10">10</option>
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                      </select>
                      <span>Entries</span>
                    </div>

                    <div class="search-box">
                      <span class="material-symbols-outlined search-icon">search</span>
                      <input type="search" v-model="search"
                        class="form-control form-control-sm search-input d-flex w-100"
                        placeholder="Search by file title, number, MDA or amount approved..." />
                    </div>
                  </div>

                  <div class="export-links">
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToPDF()">Export to
                      PDF</a>
                    <a href="#" class="export-link text-decoration-none" @click.prevent="exportToExcel()">Export to
                      Excel</a>
                  </div>

                  <div class="register-wrapper">
                    <button class="btn btn-md btn-success w-100 d-flex justify-content-between align-items-center"
                      @click="createNewMemo">
                      <span class="material-symbols-outlined align-items-center me-2">
                        add
                      </span>
                      <router-link to="/dashboard/generate/pv-memo" class="text-white text-decoration-none">Create PV
                        Memo</router-link>
                    </button>
                  </div>

                </div>
              </div>

              <!-- Table -->
              <div v-if="loading" class="loading-state text-center py-5">
                <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                <p class="mt-2 mb-0 text-muted">Loading PV memos...</p>
              </div>

              <div v-else class="table-wrapper">
                <div class="table-container">
                  <table class="responsive-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>File Title</th>
                        <th>File Number</th>
                        <th>Creation Date</th>
                        <th>Originating MDA</th>
                        <th>Amount Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="paginatedItems.length === 0">
                        <td colspan="6" class="text-center">No PV Memos to display.</td>
                      </tr>
                      <tr v-for="(pv_memo, index) in paginatedItems" :key="pv_memo.id"
                        :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                        <td data-label="S/N" class="serial-no text-center">{{ (currentPage - 1) * pageLength + index + 1
                          }}</td>
                        <td data-label="File Title" class="primary-col" title="View PV Memo"
                          @click.prevent="viewMemo(pv_memo.id)">
                          <router-link :to="{ name: 'ViewPVMemo', params: { id: pv_memo.id } }"
                            class="view-link fw-semibold text-decoration-underline" @mouseenter="showTooltip($event, pv_memo)"
                              @mouseleave="hideTooltip">
                            {{ pv_memo.file_title }}
                          </router-link>
                        </td>
                        <td data-label="File Number" class="secondary-col">{{ pv_memo.file_number }}</td>
                        <td data-label="Creation Date" class="secondary-col">{{ formatDate(pv_memo.creation_date) }}
                        </td>
                        <td data-label="Originating MDA" class="secondary-col">{{ pv_memo.originating_mda }}</td>
                        <td data-label="Amount Approved" class="secondary-col fw-semibold text-end">{{
                          formatCurrency(pv_memo.amount_approved) }}</td>
                        <!-- <td data-label="Actions" class="text-center actions-col">
                          <a href="#" class="view-link text-decoration-none"
                            @click.prevent="viewMemo(pv_memo.id)">View</a>
                        </td> -->
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Tooltip -->
                <div v-if="tooltipVisible && hoveredNotification" class="custom-tooltip" :style="{
                  top: tooltipY + 'px',
                  left: tooltipX + 'px'
                }">
                  <div v-if="isLoadingDetails">
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading details...
                  </div>
                  <div v-else>
                    <div class="tooltip-header">
                      <p><strong>Details</strong></p>
                    </div>
                    <!-- PV MEMO -->
                    <div>
                      <p><strong>File Title: </strong>{{ hoveredNotification.fileTitle || 'N/A' }}</p>
                      <p><strong>File Number: </strong>{{ hoveredNotification.fileNumber || 'N/A' }}</p>
                      <p><strong>MDA: </strong>{{ hoveredNotification.originatingMda || 'N/A' }}</p>
                      <p><strong>MDA: </strong>{{ hoveredNotification.originatingMda || 'N/A' }}</p>
                      <p v-if="hoveredNotification.pendingApproval"><strong>Old Amount Approved: </strong>{{
                        formatCurrency(hoveredNotification.oldAmountApproved || 0) }}</p>
                      <p><strong>Amount Approved: </strong>{{ formatCurrency(hoveredNotification.amountApproved || 0) }}
                      </p>
                      <p><strong>Org Code: </strong>{{ hoveredNotification.orgCode || 'N/A' }}</p>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Pagination Summary and Actions -->
              <div v-if="!loading" class="pagination-info">
                <small>
                  Showing {{ (currentPage - 1) * pageLength + 1 }} to
                  {{ Math.min(currentPage * pageLength, filteredItems.length) }} of
                  {{ filteredItems.length }} entries
                </small>

                <!-- Pagination Controls -->
                <nav class="pagination-nav">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <button class="page-link" @click.prevent="changePage(currentPage - 1)">Previous</button>
                    </li>
                    <li v-for="page in displayedPages" :key="page" class="page-item"
                      :class="{ active: currentPage === page }">
                      <button class="page-link" @click.prevent="changePage(page)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <button class="page-link" @click.prevent="changePage(currentPage + 1)">Next</button>
                    </li>
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import { debounce } from 'lodash'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs'
import { useRouter } from 'vue-router'
import '@/assets/css/table.css'
import '@/assets/css/responsive-table.css'

const router = useRouter()

const pv_memos = ref([])
const mdas = ref([])
const loading = ref(true)
const search = ref('')
const pageLength = ref(10)
const currentPage = ref(1)
const root_api_url = process.env.VUE_APP_API_ROOT_URL

const hoveredNotification = ref(null)
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)

const showTooltip = async (event, pv_memo) => {
  hoveredNotification.value = pv_memo
  tooltipVisible.value = true

  const rect = event.currentTarget.getBoundingClientRect()

  tooltipX.value = rect.left + window.scrollX
  tooltipY.value = rect.bottom + window.scrollY + 8 // small offset

  await fetchPVMemo(pv_memo.id)
}

const hideTooltip = () => {
  tooltipVisible.value = false
}

const filters = ref({
  mda: '',
  date: '',
  status: ''
})

const debouncedSearch = ref('')
watch(search, debounce((val) => {
  debouncedSearch.value = val
  currentPage.value = 1
}, 300))

watch(pageLength, () => {
  currentPage.value = 1
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

const fetchPVMemo = async (id) => {
  const token = Cookies.get('auth_token');
  try {
    const { data } = await axios.get(`${root_api_url}/payment/memo/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    hoveredNotification.value = {
      ...hoveredNotification.value,
      ...data
    }
  } catch (error) {
    console.error('Error fetching PV Memo:', error);
  }
};

const fetchMemos = async () => {
  const token = Cookies.get('auth_token')
  loading.value = true
  try {
    const response = await axios.get(`${root_api_url}/payment/memo/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    pv_memos.value = response.data.map((pv_memo) => ({
      id: pv_memo.id,
      file_title: pv_memo.fileTitle ?? '',
      file_number: pv_memo.fileNumber ?? '',
      creation_date: pv_memo.createdDate,
      originating_mda: pv_memo.originatingMda ?? '',
      amount_approved: pv_memo.amountApproved ?? 0,
      status: pv_memo.status ?? 'active'
    }))
  } catch (error) {
    console.error('Failed to fetch payment memos:', error)
    pv_memos.value = []
  } finally {
    loading.value = false
  }
}

const fetchMdas = async () => {
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    mdas.value = response.data.map(mda => ({
      id: mda.id,
      name: mda.name,
    }))
  } catch (error) {
    console.error('Failed to fetch MDAs:', error)
  }
}

const uniqueMdas = computed(() => {
  return [...mdas.value].sort((a, b) => a.name.localeCompare(b.name))
})

const mdaFilterOptions = computed(() =>
  uniqueMdas.value.map(mda => ({ id: mda.name, name: mda.name }))
)

const filteredItems = computed(() => {
  let filtered = pv_memos.value

  // Apply search filter
  if (debouncedSearch.value) {
    const term = debouncedSearch.value.toLowerCase()
    filtered = filtered.filter(pv_memo => {
      return (
        pv_memo.file_title.toLowerCase().includes(term) ||
        pv_memo.file_number.toLowerCase().includes(term) ||
        pv_memo.originating_mda.toLowerCase().includes(term) ||
        pv_memo.amount_approved.toString().includes(term)
      )
    })
  }

  // Apply MDA filter
  if (filters.value.mda) {
    filtered = filtered.filter(m => m.originating_mda === filters.value.mda)
  }

  // Apply date filter
  if (filters.value.date) {
    const now = new Date()
    filtered = filtered.filter(m => {
      const creationDate = new Date(m.creation_date)
      switch (filters.value.date) {
        case 'this_month':
          return creationDate.getMonth() === now.getMonth() &&
            creationDate.getFullYear() === now.getFullYear()
        case 'last_month': {
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1)
          return creationDate.getMonth() === lastMonth.getMonth() &&
            creationDate.getFullYear() === lastMonth.getFullYear()
        }
        case 'this_year':
          return creationDate.getFullYear() === now.getFullYear()
        default:
          return true
      }
    })
  }

  // Apply date sorting (MOST RECENT FIRST)
  filtered = [...filtered].sort((a, b) => {
    return new Date(b.creation_date) - new Date(a.creation_date)
  })

  // Apply status filter
  if (filters.value.status) {
    filtered = filtered.filter(m => m.status === filters.value.status)
  }

  return filtered
})

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageLength.value
  return filteredItems.value.slice(start, start + pageLength.value)
})

const totalPages = computed(() => {
  return Math.ceil(filteredItems.value.length / pageLength.value)
})

const displayedPages = computed(() => {
  const pages = []
  const maxDisplayed = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
  let endPage = Math.min(totalPages.value, startPage + maxDisplayed - 1)

  if (endPage - startPage < maxDisplayed - 1) {
    startPage = Math.max(1, endPage - maxDisplayed + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const clearFilters = () => {
  filters.value = {
    mda: '',
    date: '',
    status: ''
  }
  search.value = ''
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatCurrency = (amount) => {
  return '₦' + amount.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const viewMemo = (id) => {
  if (!id) return
  router.push({ name: 'ViewPVMemo', params: { id } })
}

const createNewMemo = () => {
  // Navigate to create pv_memo page
  console.log('Create new pv_memo')
}

const exportToPDF = () => {
  const doc = new jsPDF()

  // Add title
  doc.setFontSize(16)
  doc.text('Memo Directory', 14, 15)

  // Add date
  doc.setFontSize(10)
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)

  autoTable(doc, {
    startY: 30,
    head: [['File Title', 'File Number', 'Creation Date', 'Originating MDA', 'Amount Approved']],
    body: filteredItems.value.map((pv_memo) => [
      pv_memo.file_title,
      pv_memo.file_number,
      formatDate(pv_memo.creation_date),
      pv_memo.originating_mda,
      formatCurrency(pv_memo.amount_approved)
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] }
  })

  doc.save('pv_memos.pdf')
}

const exportToExcel = async () => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Memos')

  // Add headers
  worksheet.addRow(['File Title', 'File Number', 'Creation Date', 'Originating MDA', 'Amount Approved'])

  // Add data
  filteredItems.value.forEach((pv_memo) => {
    worksheet.addRow([
      pv_memo.file_title,
      pv_memo.file_number,
      formatDate(pv_memo.creation_date),
      pv_memo.originating_mda,
      formatCurrency(pv_memo.amount_approved)
    ])
  })

  // Style header row
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }

  // Auto-fit columns
  worksheet.columns.forEach(column => {
    column.width = 20
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'pv_memos.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

onMounted(async () => {
  await fetchMdas()
  await fetchMemos()
  document.title = "View PV Memos"
})

</script>

<style scoped>
/* Fix horizontal scroll - override any parent overflow restrictions */
.view-item,
.form-inner,
.form-body {
  overflow: visible !important;
}

/* Component-specific styles */
.primary-col {
  font-weight: 500;
  color: #212529;
}

.row-even {
  background-color: #ffffff;
}

.row-odd {
  background-color: var(--table-bg-head);
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.view-link {
  color: #428bca;
  font-weight: 500;
}

.view-link:hover {
  color: #2a6496;
  text-decoration: underline !important;
}

.filters-section {
  background-color: var(--table-bg-head);
  padding: 1.5rem;
  border-radius: 0.375rem;
  border: 1px solid var(--table-border);
}

.form-label {
  margin-bottom: 0.25rem;
  color: var(--color);
}

.showing-info {
  padding-left: 0.5rem;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 1000px;
}

.register-wrapper {
  display: flex;
  max-height: 35px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filters-section .row {
    gap: 0.75rem;
  }

  .table-controls-top {
    flex-direction: column;
    gap: 1rem;
  }

  .export-links {
    width: 100%;
    display: flex;
    gap: 1rem;
  }

  .export-link {
    flex: 1;
    text-align: center;
  }
}

.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background: #ffffff;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 280px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  animation: fadeIn 0.15s ease-in-out;
}

.custom-tooltip p {
  margin: 0 0 6px;
}

.custom-tooltip p:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
